const interestTags = {
  matcha: ["matcha"], coffee: ["coffee"], plants: ["plants", "botanical"],
  reading: ["reading"], desk: ["desk", "writing", "drawing"],
  incense: ["incense"], travel: ["travel", "watch"], cooking: ["kitchen"],
};

export function decodeGiftText(value) {
  const entities = { "&#39;": "'", "&quot;": '"', "&amp;": "&", "&lt;": "<", "&gt;": ">" };
  return value.replace(/&(?:#39|quot|amp|lt|gt);/g, (entity) => entities[entity]);
}

export function selectGiftPieces(pieces, answers) {
  const budget = Math.round(Number(answers.budget) * 100);
  if (!Number.isSafeInteger(budget) || budget < 100 || budget > 1000000) return [];
  if (answers.interest !== "any" && !Object.hasOwn(interestTags, answers.interest)) return [];
  const tags = interestTags[answers.interest];
  return pieces.filter((piece) => piece.handle !== answers.owned &&
    (!tags || tags.some((tag) => piece.tags.includes(tag))))
    .map((piece) => ({ ...piece, eligible: piece.variants.filter((variant) =>
      variant.available === true && Number.isSafeInteger(variant.price) &&
      variant.price > 0 && variant.price <= budget) }))
    .filter((piece) => piece.eligible.length > 0).slice(0, 3);
}

function readPieces(root) {
  return Array.from(root.querySelectorAll("template[data-gift-piece]"), (template) => ({
    handle: template.dataset.handle,
    tags: (template.dataset.tags || "").split(","),
    template,
    variants: Array.from(template.content.querySelectorAll("option"), (option) => ({
      id: option.value, price: Number(option.dataset.price),
      available: option.dataset.available === "true",
    })),
  }));
}

function mountGiftFinder(root) {
  if (root.dataset.giftMounted) return;
  root.dataset.giftMounted = "true";
  const form = root.querySelector("[data-gift-form]");
  const status = root.querySelector("[data-gift-status]");
  const heading = root.querySelector("[data-gift-heading]");
  const results = root.querySelector("[data-gift-results]");
  const requirements = root.querySelector("[data-gift-requirements]");
  const limit = root.querySelector("[data-gift-limit]");
  const submit = form.querySelector('[type="submit"]');
  let text;
  try {
    text = Object.fromEntries(Object.entries(JSON.parse(root.querySelector("[data-gift-text]").textContent))
      .map(([key, value]) => [key, typeof value === "string" ? decodeGiftText(value) : value]));
  }
  catch { return; }
  let pending;
  let revision = 0;
  form.hidden = false;

  function clear() {
    revision += 1;
    pending?.abort();
    pending = null;
    submit.disabled = false;
    results.replaceChildren();
    root.querySelector('.gift-finder__selection [data-gift-guide]')?.remove();
    requirements.replaceChildren();
    requirements.hidden = true;
    limit.hidden = true;
    heading.textContent = text.beforeTitle;
    status.textContent = text.before;
    root.removeAttribute("aria-busy");
  }
  form.addEventListener("reset", clear);
  form.addEventListener("change", clear);
  window.addEventListener("pagehide", () => { clear(); form.reset(); });
  document.addEventListener("visitorConsentCollected", (event) => {
    if (event.detail?.analyticsAllowed === false || event.detail?.marketingAllowed === false) form.reset();
  });
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    clear();
    const current = revision;
    const answers = Object.fromEntries(new FormData(form));
    const interest = form.elements.interest.selectedOptions[0].textContent;
    const recipient = form.elements.recipient.selectedOptions[0].textContent;
    pending = new AbortController();
    const controller = pending;
    const timeout = setTimeout(() => controller.abort(), 12000);
    status.textContent = text.loading;
    submit.disabled = true;
    root.setAttribute("aria-busy", "true");
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("section_id", root.dataset.sectionId);
      const response = await fetch(url, { signal: controller.signal, cache: "no-store", credentials: "same-origin" });
      if (!response.ok) throw new Error("Unavailable catalogue");
      const refreshed = new DOMParser().parseFromString(await response.text(), "text/html").querySelector("[data-gift-finder]");
      if (!refreshed || refreshed.dataset.currency !== root.dataset.currency) throw new Error("Changed catalogue context");
      const catalogue = readPieces(refreshed);
      if (!catalogue.length) throw new Error("Empty catalogue");
      if (current !== revision) return;
      const selected = selectGiftPieces(catalogue, answers);
      heading.textContent = `${text.selection} ${recipient.toLocaleLowerCase()}`;
      status.textContent = selected.length === 1 ? text.result : selected.length ? text.results.replace("{count}", String(selected.length)) : text.empty;
      ["delivery", "wrapping"].forEach((key) => {
        if (answers[key]) {
          const note = document.createElement("p");
          note.textContent = text[key];
          requirements.append(note);
        }
      });
      requirements.hidden = !requirements.childElementCount;
      selected.forEach((piece) => {
        const item = piece.template.content.firstElementChild.cloneNode(true);
        const variants = item.querySelector("[data-gift-variant]");
        Array.from(variants.options).forEach((option) => {
          const eligible = piece.eligible.some((variant) => variant.id === option.value);
          option.disabled = !eligible;
          if (option.dataset.available === "true" && !eligible) option.textContent += ` · ${text.overBudget}`;
        });
        variants.value = piece.eligible[0].id;
        const update = () => {
          const option = variants.selectedOptions[0];
          item.querySelector("[data-gift-price]").textContent = option.dataset.priceLabel;
          item.querySelectorAll("[data-gift-link], [data-gift-view], [data-gift-image]").forEach((link) => {
            const destination = new URL(link.getAttribute("href"), window.location.origin);
            destination.searchParams.set("variant", option.value);
            link.href = destination.pathname + destination.search;
          });
        };
        variants.addEventListener("change", update);
        item.querySelector("[data-gift-reason]").textContent = answers.interest === "any"
          ? text.reasonAny : text.reason.replace("{interest}", interest.toLocaleLowerCase());
        update();
        results.append(item);
      });
      limit.hidden = selected.length === 0;
      const guide = refreshed.querySelector(`[data-gift-guide="${answers.interest}"]`);
      if (guide) results.after(guide.cloneNode(true));
      heading.focus({ preventScroll: true });
      heading.scrollIntoView({ block: "start", behavior: "instant" });
    } catch {
      if (current === revision) {
        status.textContent = text.error;
        heading.focus({ preventScroll: true });
      }
    } finally {
      clearTimeout(timeout);
      if (current === revision) {
        pending = null;
        submit.disabled = false;
        root.removeAttribute("aria-busy");
      }
    }
  });
}

if (typeof document !== "undefined") {
  document.querySelectorAll("[data-gift-finder]").forEach(mountGiftFinder);
  document.addEventListener("shopify:section:load", (event) => {
    event.target.querySelectorAll("[data-gift-finder]").forEach(mountGiftFinder);
  });
}
